<script>
import { useVuelidate } from '@vuelidate/core';
import { required, url } from '@vuelidate/validators';
import { useAlert } from 'dashboard/composables';

import NextButton from 'dashboard/components-next/button/Button.vue';

export default {
  components: {
    NextButton,
  },
  props: {
    show: {
      type: Boolean,
      default: false,
    },
    mode: {
      type: String,
      default: 'create',
    },
    selectedAppData: {
      type: Object,
      default: () => ({}),
    },
  },
  emits: ['close'],
  setup() {
    return { v$: useVuelidate() };
  },
  validations: {
    app: {
      title: { required },
      content: {
        type: { required },
        url: { required, url },
      },
      icon: { required },
    },
  },
  data() {
    return {
      isLoading: false,
      app: {
        title: '',
        content: {
          type: 'frame',
          url: '',
        },
        position: 'messages',
        icon: 'i-lucide-external-link',
      },
      availableIcons: [
        'i-lucide-external-link',
        'i-lucide-link',
        'i-lucide-message-circle',
        'i-lucide-mail',
        'i-lucide-folder',
        'i-lucide-calendar',
        'i-lucide-user',
        'i-lucide-users',
        'i-lucide-building',
        'i-lucide-flag',
        'i-lucide-bell',
        'i-lucide-settings',
      ],
    };
  },
  computed: {
    header() {
      return this.isUpdateMode
        ? this.$t('INTEGRATION_SETTINGS.DASHBOARD_APPS.UPDATE.HEADER')
        : this.$t('INTEGRATION_SETTINGS.DASHBOARD_APPS.CREATE.HEADER');
    },
    isUpdateMode() {
      return this.mode === 'UPDATE';
    },
    submitButtonLabel() {
      return this.isUpdateMode
        ? this.$t('INTEGRATION_SETTINGS.DASHBOARD_APPS.UPDATE.FORM_SUBMIT')
        : this.$t('INTEGRATION_SETTINGS.DASHBOARD_APPS.CREATE.FORM_SUBMIT');
    },
  },
  mounted() {
    if (this.isUpdateMode && this.selectedAppData) {
      this.app.title = this.selectedAppData.title;
      this.app.content = this.selectedAppData.content[0];
      this.app.position = this.selectedAppData.position || 'messages';
      this.app.icon = this.selectedAppData.icon || 'i-lucide-external-link';
    }
  },
  methods: {
    closeModal() {
      // Reset the data once closed
      this.resetForm();
      this.$emit('close');
    },
    async submit() {
      try {
        this.v$.$touch();
        if (this.v$.$invalid) {
          return;
        }

        const action = this.mode.toLowerCase();
        const payload = {
          title: this.app.title,
          content: [this.app.content],
          position: this.app.position,
          icon: this.app.icon,
        };

        if (this.isUpdateMode) {
          payload.id = this.selectedAppData.id;
        }

        this.isLoading = true;
        await this.$store.dispatch(`dashboardApps/${action}`, payload);
        useAlert(this.successMessage());
        this.closeModal();
      } catch (err) {
        useAlert(this.errorMessage());
      } finally {
        this.isLoading = false;
      }
    },
    selectIcon(icon) {
      this.app.icon = icon;
    },
    resetForm() {
      this.app = {
        title: '',
        content: { type: 'frame', url: '' },
        position: 'messages',
        icon: 'i-lucide-external-link',
      };
      this.v$.$reset();
    },
    successMessage() {
      return this.isUpdateMode
        ? this.$t('INTEGRATION_SETTINGS.DASHBOARD_APPS.UPDATE.API_SUCCESS')
        : this.$t('INTEGRATION_SETTINGS.DASHBOARD_APPS.CREATE.API_SUCCESS');
    },
    errorMessage() {
      return this.isUpdateMode
        ? this.$t('INTEGRATION_SETTINGS.DASHBOARD_APPS.UPDATE.API_ERROR')
        : this.$t('INTEGRATION_SETTINGS.DASHBOARD_APPS.CREATE.API_ERROR');
    },
  },
};
</script>

<template>
  <woot-modal :show="show" :on-close="closeModal">
    <div class="flex flex-col h-auto overflow-auto">
      <woot-modal-header :header-title="header" />
      <form class="w-full space-y-4" @submit.prevent="submit">
        <woot-input
          v-model="app.title"
          :class="{ error: v$.app.title.$error }"
          class="w-full"
          :label="$t('INTEGRATION_SETTINGS.DASHBOARD_APPS.FORM.TITLE_LABEL')"
          :placeholder="
            $t('INTEGRATION_SETTINGS.DASHBOARD_APPS.FORM.TITLE_PLACEHOLDER')
          "
          :error="
            v$.app.title.$error
              ? $t('INTEGRATION_SETTINGS.DASHBOARD_APPS.FORM.TITLE_ERROR')
              : null
          "
          data-testid="app-title"
          @input="v$.app.title.$touch"
          @blur="v$.app.title.$touch"
        />
        <woot-input
          v-model="app.content.url"
          :class="{ error: v$.app.content.url.$error }"
          class="w-full"
          :label="$t('INTEGRATION_SETTINGS.DASHBOARD_APPS.FORM.URL_LABEL')"
          :placeholder="
            $t('INTEGRATION_SETTINGS.DASHBOARD_APPS.FORM.URL_PLACEHOLDER')
          "
          :error="
            v$.app.content.url.$error
              ? $t('INTEGRATION_SETTINGS.DASHBOARD_APPS.FORM.URL_ERROR')
              : null
          "
          data-testid="app-url"
          @input="v$.app.content.url.$touch"
          @blur="v$.app.content.url.$touch"
        />
        <div class="w-full">
          <label class="block text-sm font-medium text-n-slate-12 mb-2">
            {{ $t('INTEGRATION_SETTINGS.DASHBOARD_APPS.FORM.ICON_LABEL') }}
          </label>
          <div
            class="grid grid-cols-6 gap-2 max-h-40 overflow-y-auto p-2 border border-n-weak rounded-lg"
          >
            <button
              v-for="icon in availableIcons"
              :key="icon"
              type="button"
              class="flex items-center justify-center w-10 h-10 rounded-lg border-2 transition-all duration-200 hover:bg-n-alpha-2"
              :class="{
                'border-n-brand bg-n-brand/10': app.icon === icon,
                'border-n-weak hover:border-n-slate-8': app.icon !== icon,
              }"
              :title="icon.replace('i-lucide-', '')"
              @click="selectIcon(icon)"
            >
              <span :class="icon" class="text-lg" />
            </button>
          </div>
          <p class="text-xs text-n-slate-11 mt-1">
            {{ $t('INTEGRATION_SETTINGS.DASHBOARD_APPS.FORM.ICON_HELP') }}
          </p>
        </div>
        <div class="w-full">
          <label class="block text-sm font-medium text-n-slate-12 mb-2">
            {{ $t('INTEGRATION_SETTINGS.DASHBOARD_APPS.FORM.POSITION_LABEL') }}
          </label>
          <div class="space-y-2">
            <label class="flex items-center gap-2 cursor-pointer">
              <input
                v-model="app.position"
                type="radio"
                value="messages"
                class="text-n-brand focus:ring-n-brand"
              />
              <span class="text-sm text-n-slate-12">
                {{
                  $t(
                    'INTEGRATION_SETTINGS.DASHBOARD_APPS.FORM.POSITION_MESSAGES'
                  )
                }}
              </span>
            </label>
            <label class="flex items-center gap-2 cursor-pointer">
              <input
                v-model="app.position"
                type="radio"
                value="sidebar"
                class="text-n-brand focus:ring-n-brand"
              />
              <span class="text-sm text-n-slate-12">
                {{
                  $t(
                    'INTEGRATION_SETTINGS.DASHBOARD_APPS.FORM.POSITION_SIDEBAR'
                  )
                }}
              </span>
            </label>
          </div>
        </div>
        <div class="flex flex-row justify-end w-full gap-2 px-0 py-2">
          <NextButton
            faded
            slate
            type="reset"
            :label="
              $t('INTEGRATION_SETTINGS.DASHBOARD_APPS.CREATE.FORM_CANCEL')
            "
            @click.prevent="closeModal"
          />
          <NextButton
            type="submit"
            :label="submitButtonLabel"
            :disabled="v$.$invalid"
            :is-loading="isLoading"
          />
        </div>
      </form>
    </div>
  </woot-modal>
</template>
